import { GameConfig, GameState, Position, Player, PlayerType } from './types';
import { GAME_CONSTANTS } from './constants';
import { GameRenderer } from './gameRenderer';
import { InputManager } from './InputManager';

export class GameEngine {
  // Core Properties
  private renderer: GameRenderer;
  private inputManager: InputManager;
  private isRunning = false;
  private animationFrameId?: number;
  private onPositionChange?: (positions: Record<PlayerType, Position>) => void;
  private gameState: GameState;

  // Constants
  private readonly MAX_OFF_SCREEN_TIME = GAME_CONSTANTS.MAX_OFF_SCREEN_TIME;
  private readonly MAX_DISTANCE = GAME_CONSTANTS.MAX_DISTANCE;
  private readonly MAX_PELLETS = GAME_CONSTANTS.MAX_PELLETS;
  private readonly MAX_GAS = GAME_CONSTANTS.MAX_GAS;

  // Game Mechanics Constants
  private readonly PELLET_SPAWN_RATE = GAME_CONSTANTS.PELLET_SPAWN_RATE;
  private readonly GAS_DECAY_RATE = GAME_CONSTANTS.GAS_DECAY_RATE;
  private readonly PELLET_GAS_VALUE = GAME_CONSTANTS.PELLET_GAS_VALUE;

  constructor(
    ctx: CanvasRenderingContext2D,
    config: GameConfig,
    onPositionChange: (positions: Record<PlayerType, Position>) => void
  ) {
    this.renderer = new GameRenderer(ctx, config);
    this.inputManager = new InputManager();
    this.onPositionChange = onPositionChange;
    this.gameState = this.getInitialGameState();
  }

  private getInitialGameState(): GameState {
    return {
      players: {
        topdown: {
          position: { x: 0, y: 0 },
          velocity: { x: 0, y: 0 },
          type: 'topdown',
        },
        platformer: {
          position: { x: 2, y: 0 },
          velocity: { x: 0, y: 0 },
          type: 'platformer',
          isGrounded: true,
        },
      },
      pellets: [],
      offScreenTimer: 0,
      redGas: 0,
    };
  }

  public start() {
    if (this.isRunning) return;
    this.isRunning = true;
    this.gameLoop(performance.now());
  }

  public stop() {
    this.isRunning = false;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.inputManager.cleanup();
  }

  private gameLoop = (timestamp: number) => {
    if (!this.isRunning) return;

    // Check for game over condition
    if (this.checkGameOver()) {
      this.resetGame();
      return;
    }

    const newPositions = this.inputManager.update(
      timestamp,
      this.gameState.players
    );
    let positionsChanged = false;

    // Check if players are close and reset off-screen timer if they are
    if (this.getPlayerDistance() < 1) {
      this.gameState.offScreenTimer = 0;
      this.gameState.players.platformer.isGrounded = true;
    }

    // Update off-screen timer only when platformer is off-screen
    if (this.isPlayerOffScreen(this.gameState.players.platformer.position)) {
      this.gameState.offScreenTimer = Math.min(
        this.gameState.offScreenTimer + 1,
        this.MAX_OFF_SCREEN_TIME
      );
    }

    // Process movements
    if (newPositions.topdown && this.gameState.redGas > 0) {
      this.gameState.players.topdown.position = newPositions.topdown;
      this.gameState.redGas = Math.max(
        0,
        this.gameState.redGas - this.GAS_DECAY_RATE
      );
      positionsChanged = true;
    }

    if (newPositions.platformer) {
      if (this.gameState.offScreenTimer >= this.MAX_OFF_SCREEN_TIME) {
        this.gameState.players.platformer.velocity = { x: 0, y: 0 };
      } else if (!this.checkPlatformerCollision(newPositions.platformer)) {
        this.gameState.players.platformer.position = newPositions.platformer;
        positionsChanged = true;
      }
    }

    if (positionsChanged) {
      this.onPositionChange?.({
        topdown: this.gameState.players.topdown.position,
        platformer: this.gameState.players.platformer.position,
      });
    }

    // Update pellets
    this.updatePellets();

    // Draw the game state
    this.renderer.drawGame(this.gameState.players, this.gameState.pellets);

    // Request next frame
    this.animationFrameId = requestAnimationFrame(this.gameLoop);
  };

  public resize(width: number, height: number) {
    // Add resize handling if needed
  }

  private checkPlatformerCollision(pos: Position): boolean {
    const player = this.gameState.players.platformer;

    const collisionPoints = {
      feetLeft: { x: pos.x - 0.3, y: pos.y },
      feetCenter: { x: pos.x, y: pos.y },
      feetRight: { x: pos.x + 0.3, y: pos.y },

      bodyLeft: { x: pos.x - 0.4, y: pos.y + 0.5 },
      bodyRight: { x: pos.x + 0.4, y: pos.y + 0.5 },

      headLeft: { x: pos.x - 0.3, y: pos.y + 0.9 },
      headRight: { x: pos.x + 0.3, y: pos.y + 0.9 },
    };

    // Check collisions in order of priority
    const groundCollision =
      this.renderer.isLandAt(collisionPoints.feetLeft) ||
      this.renderer.isLandAt(collisionPoints.feetCenter) ||
      this.renderer.isLandAt(collisionPoints.feetRight);

    const headCollision =
      this.renderer.isLandAt(collisionPoints.headLeft) ||
      this.renderer.isLandAt(collisionPoints.headRight);

    const leftWallCollision = this.renderer.isLandAt(collisionPoints.bodyLeft);
    const rightWallCollision = this.renderer.isLandAt(
      collisionPoints.bodyRight
    );

    // Handle wall collisions first
    if (leftWallCollision || rightWallCollision) {
      // Stop horizontal movement into walls
      if (
        (leftWallCollision && player.velocity.x < 0) ||
        (rightWallCollision && player.velocity.x > 0)
      ) {
        player.velocity.x = 0;
      }

      // Wall jump
      if (this.inputManager.isKeyPressed('KeyW')) {
        player.velocity.y =
          this.inputManager.getPlatformerConfig().jumpForce * 0.8;
        player.velocity.x = leftWallCollision ? 0.2 : -0.2;
      }

      // Prevent sliding up walls
      if (!groundCollision && player.velocity.y > 0) {
        player.velocity.y = 0;
      }
    }

    // Handle ground collision
    if (groundCollision) {
      player.isGrounded = true;
      player.velocity.y = 0;
      player.position.y = Math.floor(pos.y) + 1;
    } else if (player.isGrounded && player.velocity.y <= 0) {
      player.isGrounded = false;
    }

    // Handle ceiling collision
    if (headCollision && player.velocity.y > 0) {
      player.velocity.y = 0;
    }

    // Return true if we need to prevent movement
    return (
      (groundCollision && player.velocity.y < 0) ||
      (headCollision && player.velocity.y > 0) ||
      (leftWallCollision && player.velocity.x < 0) ||
      (rightWallCollision && player.velocity.x > 0)
    );
  }

  private resetGame() {
    this.gameState = this.getInitialGameState();
  }

  public getOffScreenTimer() {
    return this.gameState.offScreenTimer;
  }

  public getMaxOffScreenTime() {
    return this.MAX_OFF_SCREEN_TIME;
  }

  public isPlayerOffScreen(pos: Position): boolean {
    const canvas = this.renderer.getCanvas();
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const tileSize = this.renderer.getTileSize();

    const screenPos = this.renderer.toCanvasSpace(
      pos,
      this.gameState.players.topdown.position,
      tileSize
    );

    const padding = tileSize * 2;
    return (
      Math.abs(screenPos.x) > centerX - padding ||
      Math.abs(screenPos.y) > centerY - padding
    );
  }

  private getPlayerDistance(): number {
    return Math.sqrt(
      Math.pow(
        this.gameState.players.platformer.position.x -
          this.gameState.players.topdown.position.x,
        2
      ) +
        Math.pow(
          this.gameState.players.platformer.position.y -
            this.gameState.players.topdown.position.y,
          2
        )
    );
  }

  private updatePellets() {
    // Always try to spawn new pellets near red player with controlled rate
    if (Math.random() < this.PELLET_SPAWN_RATE * 0.3) {
      const redPos = this.gameState.players.topdown.position;
      const spawnRadius = 8;

      const x = redPos.x + (Math.random() * 2 - 1) * spawnRadius;
      const y = redPos.y + (Math.random() * 2 - 1) * spawnRadius;

      // Remove oldest pellet if at capacity
      if (this.gameState.pellets.length >= this.MAX_PELLETS) {
        this.gameState.pellets.shift();
      }

      this.gameState.pellets.push({
        x: Math.round(x),
        y: Math.round(y),
      });
    }

    // Check collection by green (existing collection logic)
    this.gameState.pellets = this.gameState.pellets.filter((pellet) => {
      const distance = Math.sqrt(
        Math.pow(pellet.x - this.gameState.players.platformer.position.x, 2) +
          Math.pow(pellet.y - this.gameState.players.platformer.position.y, 2)
      );

      if (distance < 1) {
        this.gameState.redGas = Math.min(
          this.MAX_GAS,
          this.gameState.redGas + this.PELLET_GAS_VALUE
        );
        return false;
      }
      return true;
    });
  }

  public getRedGas(): number {
    return this.gameState.redGas;
  }

  public getPellets(): Position[] {
    return this.gameState.pellets;
  }

  private checkPlayerCollision(): boolean {
    const distance = this.getPlayerDistance();
    if (distance < 1) {
      this.resetGame();
      return true;
    }
    return false;
  }

  private checkGameOver(): boolean {
    return (
      this.gameState.offScreenTimer >= this.MAX_OFF_SCREEN_TIME &&
      this.gameState.redGas <= 0
    );
  }
}
